import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { hasPermission } from '@/auth/session';
import { PERMISSIONS } from '@/api/types';
import type { ExploreQueryResponse, ExploreSavedView, ExploreSpec } from '@/api/types';
import { BarList } from '@/components/BarList';
import { Callout } from '@/components/Callout';
import { Heatmap } from '@/components/Heatmap';
import { RangeBar } from '@/components/RangeBar';
import { Select, Segmented } from '@/components/Select';
import { TrendChart, formatCompact } from '@/components/TrendChart';
import { useDateRange } from '@/hooks/useOverview';
import { ConfirmDialog } from '@/components/ops/Modal';

const NO_BREAKDOWN = '';

/**
 * The explore surface: pick a dimension, a measure, a range and an optional breakdown, and get an
 * answer — without writing an endpoint and a page for it.
 *
 * Three deliberate absences, all of them the point:
 * - The dropdowns come from the server's catalog. Nothing here names a column, so the catalog can
 *   grow without touching this file — and nothing outside the catalog can be asked for.
 * - There is no chart picker. The server chooses from the shape of the result (time → trend, one
 *   dimension → bars, two → grid), so a shape cannot be drawn as something it is not.
 * - There is no join builder, pivot, or scheduler. The ceiling is written at the top of
 *   `ExploreCompiler` on the server; for everything above it, use the BI tool.
 */
export function ExplorePage() {
  const canRead = hasPermission(PERMISSIONS.exploreRead);
  const canManage = hasPermission(PERMISSIONS.exploreManage);
  const canExport = hasPermission(PERMISSIONS.telemetryExport);

  const queryClient = useQueryClient();
  const range = useDateRange();

  const [dimension, setDimension] = useState('date');
  const [measure, setMeasure] = useState('events');
  const [breakdown, setBreakdown] = useState(NO_BREAKDOWN);
  const [includeInternal, setIncludeInternal] = useState(false);

  // Filters have no builder UI in v1 — the four controls above are the whole surface — but a
  // saved view can carry them, so they are kept in state and shown as removable chips.
  const [filters, setFilters] = useState<ExploreSpec['filters']>([]);

  const [viewName, setViewName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  // Delete and export fail with an inline message too — never an unhandled rejection that leaves the button looking done.
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<ExploreSavedView | null>(null);
  const [exporting, setExporting] = useState(false);

  const catalog = useQuery({
    queryKey: ['explore', 'catalog'],
    queryFn: api.explore.catalog,
    enabled: canRead,
    staleTime: Infinity,
  });

  const views = useQuery({
    queryKey: ['explore', 'views'],
    queryFn: api.explore.listViews,
    enabled: canRead,
  });

  const spec: ExploreSpec = useMemo(() => ({
    dimension,
    measure,
    from: range.from,
    to: range.to,
    breakdown: breakdown === NO_BREAKDOWN ? null : breakdown,
    filters,
    include_internal: includeInternal,
  }), [dimension, measure, range.from, range.to, breakdown, filters, includeInternal]);

  const result = useQuery({
    queryKey: ['explore', 'query', spec],
    queryFn: () => api.explore.query(spec),
    enabled: canRead && catalog.isSuccess,
    retry: false,
    placeholderData: (previous) => previous,
  });

  if (!canRead) {
    return (
      <Callout tone="info" title="Explore needs the explore:read permission">
        Your role does not include the explore surface. The dashboards in the sidebar carry the
        curated answers; explore is for composing new questions.
      </Callout>
    );
  }

  const dimensionOptions = (catalog.data?.dimensions ?? []).map((d) => ({ value: d.key, label: d.label }));
  const measureOptions = (catalog.data?.measures ?? []).map((m) => ({ value: m.key, label: m.label }));
  const measureLabel = measureOptions.find((m) => m.value === measure)?.label ?? measure;

  function loadView(view: ExploreSavedView) {
    setDimension(view.spec.dimension);
    setMeasure(view.spec.measure);
    setBreakdown(view.spec.breakdown ?? NO_BREAKDOWN);
    setIncludeInternal(view.spec.include_internal ?? false);
    setFilters(view.spec.filters ?? []);
    range.setRange(view.spec.from, view.spec.to);
  }

  async function saveView() {
    if (!viewName.trim()) return;
    setSaveError(null);
    try {
      await api.explore.saveView(viewName.trim(), spec);
      setViewName('');
      await queryClient.invalidateQueries({ queryKey: ['explore', 'views'] });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Could not save the view.');
    }
  }

  async function deleteView(id: string) {
    setActionError(null);
    try {
      await api.explore.deleteView(id);
      await queryClient.invalidateQueries({ queryKey: ['explore', 'views'] });
    } catch (e) {
      setActionError(e instanceof Error ? `Could not delete the view: ${e.message}` : 'Could not delete the view.');
    }
  }

  async function exportCsv() {
    setExporting(true);
    setActionError(null);
    try {
      await api.explore.export(spec);
    } catch (e) {
      setActionError(e instanceof Error ? `Could not export: ${e.message}` : 'Could not export.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="t-display" style={{ margin: 0 }}>Explore</h1>
          <p className="t-body muted" style={{ margin: '4px 0 0' }}>
            Ask a new question of the cross-tenant rollups without writing an endpoint for it.
          </p>
        </div>
      </div>

      <RangeBar
        days={range.days}
        setDays={range.setDays}
        from={range.from}
        to={range.to}
        setRange={range.setRange}
        isCustomRange={range.isCustomRange}
        actions={canExport ? (
          <button className="btn btn-ghost" onClick={exportCsv} disabled={exporting || !result.data}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        ) : undefined}
      />

      <div className="filterbar" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <Select
          label="Dimension"
          value={dimension}
          onChange={(value) => {
            setDimension(value);
            // A breakdown identical to the new dimension would be refused server-side; clear it
            // here so the refusal never has to happen.
            if (breakdown === value) setBreakdown(NO_BREAKDOWN);
          }}
          options={dimensionOptions}
          inline
        />
        <Select label="Measure" value={measure} onChange={setMeasure} options={measureOptions} inline />
        <Select
          label="Breakdown"
          value={breakdown}
          onChange={setBreakdown}
          options={dimensionOptions.filter((d) => d.value !== dimension)}
          placeholder="None"
          inline
        />
        <Segmented
          label="Scope"
          value={includeInternal ? 'all' : 'customers'}
          onChange={(value) => setIncludeInternal(value === 'all')}
          options={[
            { value: 'customers', label: 'Customers' },
            { value: 'all', label: '+ internal' },
          ]}
          inline
        />
      </div>

      {(filters ?? []).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {(filters ?? []).map((f) => (
            <span key={f.dimension} className="chip chip-off">
              {f.dimension}: {f.values.join(', ')}
              <button
                type="button"
                className="btn btn-ghost"
                style={{ padding: '0 4px', marginLeft: 4 }}
                onClick={() => setFilters((current) => (current ?? []).filter((x) => x.dimension !== f.dimension))}
                aria-label={`Remove ${f.dimension} filter`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      {(views.data ?? []).length > 0 || canManage ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', margin: '12px 0' }}>
          <span className="t-label muted">Saved views</span>
          {(views.data ?? []).map((view) => (
            <span key={view.id} className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ padding: '2px 6px' }}
                onClick={() => loadView(view)}
                title={`Saved by ${view.created_by}`}
              >
                {view.name}
              </button>
              {canManage && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ padding: '0 4px' }}
                  onClick={() => setDeleting(view)}
                  aria-label={`Delete saved view ${view.name}`}
                >
                  ✕
                </button>
              )}
            </span>
          ))}
          {(views.data ?? []).length === 0 && <span className="t-body subtle">none yet</span>}
          {canManage && (
            <span style={{ display: 'inline-flex', gap: 8, marginLeft: 'auto' }}>
              <input
                className="input"
                style={{ width: 200 }}
                placeholder="Name this view"
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void saveView(); }}
              />
              <button className="btn btn-primary" onClick={saveView} disabled={!viewName.trim()}>
                Save view
              </button>
            </span>
          )}
        </div>
      ) : null}

      {saveError && <Callout tone="error" title="Could not save the view">{saveError}</Callout>}
      {actionError && <Callout tone="error" title="That did not work">{actionError}</Callout>}
      {deleting && (
        <ConfirmDialog title={`Delete the saved view “${deleting.name}”?`} confirmLabel="Delete view" destructive
          onConfirm={() => { const id = deleting.id; setDeleting(null); void deleteView(id); }} onCancel={() => setDeleting(null)}>
          <p className="t-body" style={{ margin: 0 }}>It is shared: it disappears for every admin.</p>
        </ConfirmDialog>
      )}
      {catalog.isError && (
        <Callout tone="error" title="The list of questions could not be loaded">
          Nothing can be asked until it loads.{' '}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void catalog.refetch()}>Retry</button>
        </Callout>
      )}

      {result.isError && (
        <Callout tone="error" title="The query was refused">
          {result.error instanceof Error ? result.error.message : 'Unknown error.'}
        </Callout>
      )}

      {result.data?.truncated && (
        <Callout tone="warning" title={`Showing the top ${result.data.row_cap.toLocaleString('en-US')} rows`}>
          The result was cut at the row cap — what is below is the largest slice, not the whole
          answer. Narrow the range or add a breakdown filter, or open the question in the BI tool.
        </Callout>
      )}

      <div className={`card${result.isFetching ? ' is-refetching' : ''}`}>
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>{measureLabel}</h2>
          <span className="t-label subtle">
            {result.data
              ? `${result.data.rows.length.toLocaleString('en-US')} rows`
              : result.isFetching ? 'loading' : result.isError || catalog.isError ? 'no answer' : ''}
          </span>
        </div>
        <div className="card-body">
          {result.isLoading && <div className="empty-state"><span className="spinner" /></div>}
          {result.data && <ExploreChart data={result.data} measureLabel={measureLabel} />}
        </div>
      </div>

      {result.data && result.data.rows.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="t-heading" style={{ margin: 0 }}>Rows</h2>
            <span className="t-label subtle">{result.data.from} → {result.data.to}</span>
          </div>
          <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>{dimensionOptions.find((d) => d.value === result.data!.dimension)?.label ?? result.data.dimension}</th>
                  {result.data.breakdown && (
                    <th>{dimensionOptions.find((d) => d.value === result.data!.breakdown)?.label ?? result.data.breakdown}</th>
                  )}
                  <th className="num">{measureLabel}</th>
                </tr>
              </thead>
              <tbody>
                {result.data.rows.map((row, i) => (
                  <tr key={`${row.key}|${row.break_key ?? ''}|${i}`}>
                    <td>{row.label}</td>
                    {result.data!.breakdown && <td>{row.break_label}</td>}
                    <td className="num">{formatCompact(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="t-caption subtle" style={{ marginTop: 16 }}>
        For joins, pivots and scheduled reports, use the BI tool.
      </p>
    </>
  );
}

/**
 * Draws whatever the server said the shape is. There is deliberately no chart picker — the shape
 * of the result decides, so a heatmap's data cannot be stretched into a single trend line.
 */
function ExploreChart({ data, measureLabel }: { data: ExploreQueryResponse; measureLabel: string }) {
  if (data.rows.length === 0) {
    return <div className="empty-state t-body">Nothing matched this question in this range.</div>;
  }

  switch (data.chart) {
    case 'trend':
      return (
        <TrendChart
          series={[{
            key: data.measure,
            label: measureLabel,
            color: 'var(--series-1)',
            points: data.rows.map((r) => ({ date: r.key, value: r.value })),
          }]}
        />
      );

    case 'heatmap': {
      const rowKeys = [...new Set(data.rows.map((r) => r.key))];
      const colKeys = [...new Set(data.rows.map((r) => r.break_key ?? ''))];
      const rowLabels = new Map(data.rows.map((r) => [r.key, r.label]));
      const colLabels = new Map(data.rows.map((r) => [r.break_key ?? '', r.break_label ?? '']));

      return (
        <Heatmap
          rowKeys={rowKeys}
          colKeys={colKeys}
          cells={data.rows.map((r) => ({ row: r.key, col: r.break_key ?? '', value: r.value }))}
          rowLabel={(key) => rowLabels.get(key) ?? key}
          colLabel={(key) => colLabels.get(key) ?? key}
          metricLabel={measureLabel}
        />
      );
    }

    default:
      return (
        <BarList
          rows={data.rows.map((r) => ({ label: r.label, value: r.value }))}
          limit={15}
          emptyLabel="Nothing matched this question in this range."
        />
      );
  }
}
